import { Flex } from '@chakra-ui/react';
import { FormFeedback, OutlineCard, TextArea } from '@morressier/alembic';
import { useFormContext, FieldError, UseFormSetValue } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import {
  FormFieldPreviouslySubmitted,
  FormFieldConflictOfInterestForAuthor,
  FormFieldPreprint,
  FormFieldSharedDataset,
  FormFieldEthicalApproval,
  FormFieldEthicsApproval
} from '@publishing-engine/domain/types/article';
import { RadioGroup } from 'src/components/RadioGroup';
import { get } from 'lodash';
import { getValidationTypedMessage } from 'src/components/FormField/common/errorMessages';

const RadioGroupWithTextArea = ({
  config,
  placeholder,
  setValue
}: {
  config:
    | FormFieldPreviouslySubmitted
    | FormFieldConflictOfInterestForAuthor
    | FormFieldPreprint
    | FormFieldSharedDataset
    | FormFieldEthicalApproval
    | FormFieldEthicsApproval;
  placeholder?: string;
  setValue: UseFormSetValue<ISubmissionFormData>;
}) => {
  const {
    formState: { errors },
    register,
    watch,
    control
  } = useFormContext<ISubmissionFormData>();
  const {
    params: { validations, labels },
    caseDataFields: { main: field, dependsOn }
  } = config;
  const dependsOnField = `${field}.${dependsOn}` as const;
  const mainField = `${field}.value` as const;

  const error = get(errors, field) as FieldError;

  const exists = watch(dependsOnField);
  const fieldData = watch(mainField);

  const getErrorMessage = (label?: string) => {
    if (!label) return null;

    const value = label.toLowerCase();

    switch (value) {
      case 'conflict of interest':
        return `Please indicate whether this manuscript has a ${value}`;

      case 'shared dataset':
        return `Please indicate whether this paper is part of a ${value}`;

      case 'ethical approval':
        return 'Ethical Approval is required';

      case 'ethics approval':
        return 'Ethics Approval is required';

      case 'preprint':
        return 'Please indicate if this paper is available on a preprint server';

      default:
        return `Please indicate whether this manuscript has been ${value} to this journal`;
    }
  };

  const errorMessage =
    get(errors, dependsOnField) && getErrorMessage(labels?.main);

  return (
    <Flex flexDirection="column" gap={8} width="100%">
      <OutlineCard padding={4}>
        <Flex flexDirection="column" gap={4}>
          <FieldLabel
            isRequired={validations.required}
            label={labels.main}
            subText={labels.subtitle}
            helpText={labels.helpText}
          />

          {labels.main === 'Ethics Approval' ? (
            <RadioGroup
              name={dependsOnField}
              control={control}
              options={[
                {
                  value: false,
                  children: labels.options['false']?.main
                },
                {
                  value: true,
                  children: labels.options['true']?.main
                }
              ]}
              onChange={() => {
                setValue(mainField, '');
              }}
            />
          ) : (
            <RadioGroup
              name={dependsOnField}
              control={control}
              options={[
                {
                  value: true,
                  children: labels.options['true']?.main
                },
                {
                  value: false,
                  children: labels.options['false']?.main
                }
              ]}
              onChange={() => {
                setValue(mainField, '');
              }}
            />
          )}

          {errorMessage && (
            <FormFeedback variant="error">{errorMessage}</FormFeedback>
          )}

          {exists === true &&
          (labels.section.title.length > 0 ||
            labels.section.explanation.length > 0) ? (
            <Flex flexDirection="column" gap={2}>
              <FieldLabel
                label={labels.section.explanation}
                subText={''}
                isRequired
                helpText={labels.helpText}
              />
              <TextArea
                value={fieldData || ''}
                placeholder={placeholder || ''}
                {...register(mainField, {
                  required: validations.required,
                  maxLength: validations.maxLength
                })}
                id={labels.main}
                aria-label={labels.main}
                isInvalid={!!error}
                errorMessage={
                  error &&
                  getValidationTypedMessage(
                    labels.main === 'Previously Submitted'
                      ? 'Manuscript ID'
                      : labels.main === 'Preprint'
                      ? 'Preprint Link'
                      : labels.main,
                    validations.kind,
                    validations,
                    error
                  )
                }
              />
            </Flex>
          ) : exists === false && labels?.sectionNo ? (
            <Flex flexDirection="column" gap={2}>
              <FieldLabel
                label={labels?.sectionNo?.explanation ?? ''}
                subText={''}
                isRequired
                helpText={labels.helpText}
              />
              <TextArea
                value={fieldData || ''}
                id={labels.main}
                aria-label={labels.main}
                placeholder={placeholder || ''}
                {...register(mainField, {
                  required: validations.required,
                  maxLength: validations.maxLength
                })}
                isInvalid={!!error}
                errorMessage={
                  error &&
                  getValidationTypedMessage(
                    labels.main === 'Previously Submitted'
                      ? 'Manuscript ID'
                      : labels.main === 'Preprint'
                      ? 'Preprint Link'
                      : labels.main,
                    validations.kind,
                    validations,
                    error
                  )
                }
              />
            </Flex>
          ) : null}
        </Flex>
      </OutlineCard>
    </Flex>
  );
};

export default RadioGroupWithTextArea;
